import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  Search as SearchIcon,
  Refresh,
  Inbox,
  RequestQuote,
  EmojiEvents,
  FilterList,
  Assignment,
} from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  CodeCell,
  TypePill,
  StatusDot,
  StatusTone,
  TableEmptyState,
  ErrorState,
} from '../../components/common/pageKit';
import { api } from '../../services/apiClient';

// ---------------------------------------------------------------------------
// Customer Enquiry list -- front door of the sell-side pipeline. Mirrors
// RfqPage.tsx's shape: summary stat cards computed client-side (the backend
// summary shape isn't guaranteed), a filter bar, a single results table, row
// click -> detail.
// ---------------------------------------------------------------------------

type EnquiryStatus = 'OPEN' | 'QUOTED' | 'WON' | 'LOST';
type EnquiryPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
type EnquirySource = 'DIRECT' | 'REFERRAL' | 'WEBSITE' | 'EXHIBITION' | 'CAMPAIGN' | 'EXISTING_CUSTOMER' | 'OTHER';

interface EnquiryRow {
  id: number;
  enquiry_number: string;
  customer_id: number | string;
  customer_name?: string;
  enquiry_date: string;
  valid_until?: string | null;
  source: EnquirySource;
  priority: EnquiryPriority;
  status: EnquiryStatus;
  item_count?: number;
}

const STATUS_OPTIONS: EnquiryStatus[] = ['OPEN', 'QUOTED', 'WON', 'LOST'];

const STATUS_TONE: Record<EnquiryStatus, StatusTone> = {
  OPEN: 'info',
  QUOTED: 'warning',
  WON: 'success',
  LOST: 'error',
};

const PRIORITY_TINT: Record<EnquiryPriority, string> = {
  LOW: '#64748b',
  MEDIUM: '#0284c7',
  HIGH: '#d97706',
  URGENT: '#dc2626',
};

const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('en-IN') : '—');

const CustomerEnquiryPage: React.FC = () => {
  const navigate = useNavigate();

  const [enquiries, setEnquiries] = useState<EnquiryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<EnquiryStatus | ''>('');
  const [search, setSearch] = useState('');

  const fetchAll = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<{ enquiries: EnquiryRow[]; count: number }>('/customer-enquiries')
      .then((res) => setEnquiries(res.enquiries || []))
      .catch((err: any) => setError(err.message || 'Failed to load customer enquiries'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const handle = setTimeout(fetchAll, search ? 350 : 0);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAll]);

  const filtered = enquiries.filter((e) => {
    const matchesStatus = !statusFilter || e.status === statusFilter;
    const matchesSearch =
      !search ||
      e.enquiry_number?.toLowerCase().includes(search.toLowerCase()) ||
      e.customer_name?.toLowerCase().includes(search.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const totalCount = enquiries.length;
  const openCount = enquiries.filter((e) => e.status === 'OPEN').length;
  const quotedCount = enquiries.filter((e) => e.status === 'QUOTED').length;
  const wonCount = enquiries.filter((e) => e.status === 'WON').length;

  const hasFilters = !!(statusFilter || search);
  const clearFilters = () => {
    setStatusFilter('');
    setSearch('');
  };

  return (
    <Box>
      <PageHeader
        title="Customer Enquiries"
        subtitle="Capture what customers are asking about, then move it into a formal quotation"
        actions={
          <>
            <IconButton onClick={fetchAll} disabled={loading}>
              <Refresh />
            </IconButton>
            <Button variant="contained" startIcon={<Add />} size="small" onClick={() => navigate('/sales/enquiries/new')}>
              New Enquiry
            </Button>
          </>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 2.5 }}>
        <StatCard title="Total Enquiries" value={loading ? null : totalCount} icon={<Assignment />} color="#2563eb" />
        <StatCard title="Open" value={loading ? null : openCount} icon={<Inbox />} color="#0284c7" />
        <StatCard title="Quoted" value={loading ? null : quotedCount} icon={<RequestQuote />} color="#d97706" />
        <StatCard title="Won" value={loading ? null : wonCount} icon={<EmojiEvents />} color="#059669" />
      </Box>

      <FilterBar>
        <TextField
          size="small"
          label="Search"
          placeholder="Enquiry # or customer"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as EnquiryStatus | '')}
          sx={{ minWidth: 170 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FilterList sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>

        <FilterSpacer />

        {hasFilters && (
          <Button size="small" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
      </FilterBar>

      <TableSurface>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                {['Enquiry', 'Customer', 'Source', 'Priority', 'Status', 'Items', 'Valid Until'].map((h, i) => (
                  <TableCell key={h} align={i === 5 ? 'right' : 'left'} sx={headerCellSx}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, border: 0 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ border: 0 }}>
                    <ErrorState message={error} onRetry={fetchAll} />
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ border: 0 }}>
                    <TableEmptyState title="No enquiries match these filters" hint="Clear the filters, or create a new enquiry." />
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((e) => (
                  <TableRow
                    key={e.id}
                    sx={{ ...bodyRowSx, cursor: 'pointer' }}
                    hover
                    onClick={() => navigate(`/sales/enquiries/${e.id}`)}
                  >
                    <TableCell sx={{ py: 1.5 }}>
                      <CodeCell code={e.enquiry_number} tint={PRIORITY_TINT[e.priority] || '#475569'} icon={<Inbox fontSize="small" />} />
                    </TableCell>
                    <TableCell>{e.customer_name || '—'}</TableCell>
                    <TableCell>
                      <TypePill label={e.source?.replace(/_/g, ' ') || '—'} tint="#7c3aed" />
                    </TableCell>
                    <TableCell>
                      <TypePill label={e.priority} tint={PRIORITY_TINT[e.priority] || '#475569'} />
                    </TableCell>
                    <TableCell>
                      <StatusDot label={e.status} tone={STATUS_TONE[e.status]} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {e.item_count ?? '—'}
                    </TableCell>
                    <TableCell>{fmtDate(e.valid_until)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>
    </Box>
  );
};

export default CustomerEnquiryPage;
